/**
 * Der Offline-Nachweis auf dem Zielgeraet (PV-B30).
 *
 * Die Spec macht das zur Bedingung, bevor Stufe 1 als fertig gilt — und zwar
 * auf dem Geraet, das spaeter mitfaehrt, nicht am Entwicklungsrechner. Der
 * Grund steht in Kapitel B: Das Betriebssystem darf lokal abgelegte Daten
 * verwerfen, und Apple ist dabei strenger als Android und Windows. Faellt die
 * Pruefung durch, traegt der ganze Ansatz nicht.
 *
 * Die entscheidende Zahl ist der Bestandsvergleich. Alles andere kann man
 * behaupten; ob nach einer Nacht im Flugmodus noch dieselbe Anzahl Projekte,
 * Kacheln und Fotos da ist, kann nur das Geraet selbst nachzaehlen.
 */

import { useEffect, useState } from 'react';

import { geraetename } from '../daten/abgleich.ts';
import {
  beginne,
  bestandAufnehmen,
  leseNachweis,
  schliesseAb,
  setzeSchritt,
  vergleiche,
  type Bestandsaufnahme,
  type Offlinenachweis as Nachweis,
} from '../daten/offlinenachweis.ts';
import { alsMb } from '../daten/speicher.ts';

const FELDNAMEN: Record<keyof Bestandsaufnahme, string> = {
  projekte: 'Projekte',
  flaechen: 'Flächen',
  belegungen: 'Belegungen',
  module: 'Modulpool',
  gebaeude: 'Gebäudeumrisse',
  fotos: 'Fotos',
  kachelprojekte: 'Projekte mit Karte',
  belegtBytes: 'Belegter Speicher',
};

export function Offlinenachweis(): React.JSX.Element {
  const [nachweis, setNachweis] = useState<Nachweis | null>(null);
  const [jetzt, setJetzt] = useState<Bestandsaufnahme | null>(null);

  useEffect(() => {
    void leseNachweis(geraetename()).then(setNachweis);
    void bestandAufnehmen().then(setJetzt);
  }, []);

  if (!nachweis) return <p>Einen Moment …</p>;

  const vergleich = vergleiche(
    nachweis.bestandVorher,
    nachweis.bestandNachher ?? jetzt ?? undefined,
  );
  const verloren = vergleich.filter((v) => v.verloren);
  const erledigt = nachweis.schritte.filter((s) => s.stand === 'erledigt').length;

  return (
    <section>
      <h2>Offline-Nachweis</h2>
      <p className="klein">
        Auf dem Gerät, das mitfährt — nicht am Entwicklungsrechner. Die Spec macht das zur
        Bedingung, bevor Stufe 1 als fertig gilt.
      </p>

      <dl className="zahlenblock">
        <div>
          <dt>Gerät</dt>
          <dd>{nachweis.geraet}</dd>
        </div>
        <div>
          <dt>Schritte</dt>
          <dd>
            {erledigt} von {nachweis.schritte.length}
          </dd>
        </div>
      </dl>

      {!nachweis.begonnenAm ? (
        <>
          <div className="hinweis">
            Vor dem Start: die Projekte des Tages vorbereiten. Der Bestand von diesem Moment ist die
            Messlatte für den Tag danach.
          </div>
          <button className="wichtig" onClick={() => void beginne(geraetename()).then(setNachweis)}>
            Prüfung beginnen
          </button>
        </>
      ) : (
        <>
          <p className="klein">
            Begonnen am {new Date(nachweis.begonnenAm).toLocaleString('de-AT')}
            {nachweis.startOhneVerbindungAm && (
              <>
                {' · '}ohne Verbindung gestartet am{' '}
                {new Date(nachweis.startOhneVerbindungAm).toLocaleString('de-AT')}
              </>
            )}
          </p>

          {nachweis.schritte.map((schritt) => (
            <div
              key={schritt.schluessel}
              style={{ borderTop: '1px solid var(--linie)', padding: '0.6rem 0' }}
            >
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'baseline' }}>
                <span style={{ flex: 1 }}>
                  {schritt.text}
                  {schritt.hinweis && (
                    <span style={{ display: 'block', color: 'var(--grau)', fontSize: '0.75rem' }}>
                      {schritt.hinweis}
                    </span>
                  )}
                  {schritt.am && (
                    <span style={{ display: 'block', color: 'var(--grau)', fontSize: '0.7rem' }}>
                      {new Date(schritt.am).toLocaleString('de-AT')}
                      {schritt.bemerkung ? ` · ${schritt.bemerkung}` : ''}
                    </span>
                  )}
                </span>
                {(['erledigt', 'fehlgeschlagen'] as const).map((stand) => (
                  <button
                    key={stand}
                    aria-pressed={schritt.stand === stand}
                    onClick={() =>
                      void setzeSchritt(nachweis, schritt.schluessel, stand).then(setNachweis)
                    }
                    style={
                      stand === 'fehlgeschlagen' && schritt.stand === stand
                        ? {
                            background: 'var(--waerme)',
                            color: '#fff',
                            borderColor: 'var(--waerme)',
                          }
                        : undefined
                    }
                  >
                    {stand === 'erledigt' ? 'erledigt' : 'gescheitert'}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* --- Der eigentliche Beweis ---------------------------------- */}
          <h3 style={{ marginTop: '1.5rem' }}>Hat das Gerät den Bestand behalten?</h3>
          {vergleich.length === 0 ? (
            <p className="klein">Noch kein Vergleich möglich.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th style={kopf}>Bestand</th>
                  <th style={{ ...kopf, textAlign: 'right' }}>vorher</th>
                  <th style={{ ...kopf, textAlign: 'right' }}>jetzt</th>
                </tr>
              </thead>
              <tbody>
                {vergleich.map((zeile) => (
                  <tr key={zeile.feld}>
                    <td style={zelle}>{FELDNAMEN[zeile.feld]}</td>
                    <td style={{ ...zelle, textAlign: 'right' }}>{zeile.vorher}</td>
                    <td
                      style={{
                        ...zelle,
                        textAlign: 'right',
                        color: zeile.verloren ? 'var(--waerme)' : undefined,
                      }}
                    >
                      {zeile.nachher}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {verloren.length > 0 && (
            <div className="hinweis">
              Das Gerät hat Daten verworfen ({verloren.map((v) => FELDNAMEN[v.feld]).join(', ')}).
              Damit ist es für den Außendienst nicht verlässlich — genau der Fall, vor dem die
              Spec warnt.
            </div>
          )}

          {nachweis.bestandVorher?.belegtBytes !== undefined && (
            <p className="klein">
              Belegter Speicher beim Start: {alsMb(nachweis.bestandVorher.belegtBytes)}
              {jetzt?.belegtBytes !== undefined && <> · jetzt {alsMb(jetzt.belegtBytes)}</>}
            </p>
          )}

          <div className="knopfreihe" style={{ marginTop: '1.25rem' }}>
            <button
              className="wichtig"
              onClick={() => void schliesseAb(nachweis).then(setNachweis)}
            >
              Prüfung abschließen
            </button>
            <button onClick={() => void beginne(geraetename()).then(setNachweis)}>
              Neu beginnen
            </button>
            <button onClick={() => window.print()}>Drucken</button>
          </div>

          {nachweis.ergebnis && (
            <div
              className="hinweis"
              style={
                nachweis.ergebnis === 'bestanden'
                  ? { borderLeftColor: 'var(--energie)' }
                  : undefined
              }
            >
              {nachweis.ergebnis === 'bestanden'
                ? `Bestanden am ${new Date(nachweis.abgeschlossenAm!).toLocaleString('de-AT')}. Das Gerät hält den Bestand.`
                : 'Nicht bestanden. Entweder fehlen Schritte oder das Gerät hat Daten verworfen.'}
            </div>
          )}
        </>
      )}
    </section>
  );
}

// Kopf und Zelle kommen aus dem CSS (th, td); hier bleibt nur die Ausrichtung.
const kopf: React.CSSProperties = {};

const zelle: React.CSSProperties = {};
